import { AppConfig } from '../../shared/config/app-config';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
    CreateCustomTransactionField,
    CustomTransactionField,
    LoginOptionTypeResponse,
    NewPasscodePayload,
    PasscodeChangeInitiatorObj,
    PasscodeOtpPayload,
    PasscodeOtpResponse,
} from '../models/settings.model';
import { LocalStorageService } from './local-storage.service';
import { StorageKeys } from '../../shared/models/storage-keys';
import { TimelineModel } from '../../shared/models/timeline.model';
import { TwoFactorAuthenticationStartResponse } from '../../shared/models/two-factor-authentication.model';
import { StringHelper } from '../helpers/strings.helper';
import { DeviceHelperService } from './device-helper.service';
import { TwoFactorGenerationPayload } from '../../shared/models/otp.model';
import { GenerateReferenceApiPayload } from '../../shared/models/device.model';

@Injectable({
    providedIn: 'root',
})
export class SettingsService {

    private subject = new BehaviorSubject<string>('');
    settingRouteControl$: Observable<string> = this.subject.asObservable();

    constructor(
        private apiClientHelper: ApiClientHelper,
        private config: AppConfig,
        private localStorage: LocalStorageService,
        public deviceHelper: DeviceHelperService,
    ) { }

    setSettingRoute(value: string): void {
        this.subject.next(value);
    }

    triggerPasswordChangeOtp() {
        // deprecated
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/settings/password-change/otp/generate`;
        return this.apiClientHelper.postForPromise(url, {}, false);
    }

    updatePassword(payload: PasswordChangePayload) {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/settings/password-change/otp/validate`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    updatePasswordV2(payload: PasswordChangePayload) {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v2/settings/password`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    recoverPasscode(payload: any) {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/recover-passcode`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    triggerPinChangeOtp() {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/settings/transaction-pin-change/otp/generate`;
        return this.apiClientHelper.postForPromise(url, {}, false);
    }

    triggerResetPinOtp() {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/settings/transaction-pin-reset/otp/generate`;
        return this.apiClientHelper.postForPromise(url, {}, false);
    }

    triggerResetPinOtpForBusinessPin() {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/profiles/agents/send-pin-reset-otp`;
        return this.apiClientHelper.getForPromise(url);
    }

    triggerResetPinOtpV2(params) {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/agent-contracts/terminal-pin-change/otp/generate`;
        return this.apiClientHelper.postForPromise(url, params, false);
    }

    resetPin(payload) {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/settings/transaction-pin-reset`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    updatePin(payload: PinChangePayload) {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/settings/transaction-pin-change/otp/validate`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    validateOtpOptionalLivelinessCheck(payload) {
        const url = `${this.config.MONIEPOINT_HOST}/api/v2/settings/transaction-pin-reset/otp/validate`;
        return this.apiClientHelper.newPostAsFormDataForPromise(url, payload, false);
    }

    validateOtpForPasscodeRecovery(payload: { phoneNumber: string; otp: string }) {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/recover-passcode/otp/validate`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    updateTerminalPin(payload) {
        const url = `${this.config.MONIEPOINT_HOST}/api/v1/agent-contracts/update-pin`;
        return this.apiClientHelper.putForPromise(url, payload);
    }

    triggerOtpV2() {
        const url = `${this.config.MONIEPOINT_HOST}/api/v2/profiles/aggregators/send-pin-reset-otp`;
        return this.apiClientHelper.getForPromise(url);
    }

    resetPinV2(payload) {
        const url = `${this.config.MONIEPOINT_HOST}/api/v2/profiles/aggregators/pin`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    setUserPin(payload) {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/user-pin`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    resetUserPin(payload) {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/user-pin-reset`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    resetUserPinV2(payload) {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v2/settings/user-pin`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    triggerUserSetPinOtp() {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/user-pin/otp/generate`;
        return this.apiClientHelper.postForPromise(url, {}, false);
    }

    updatePinBasedOnOption(option, data): Observable<any> {
        let url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/`;
        // use user pin to set business pin
        switch (option) {
            case 3:
                url = url + 'user-pin/set-business-pin';
                break;
            // use business PIn to set user pin
            case 1:
                url = url + 'business-pin/set-user-pin';
                break;
            // BO  pin to set user pin
            case 2:
                url = url + 'business-owner-pin/set-user-pin';
        }
        return this.apiClientHelper.postForObservable(url, data);
    }

    triggerUserResetPinOtp() {
        // deprecated
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/user-pin-reset/otp/generate`;
        return this.apiClientHelper.postForPromise(url, {}, false);
    }

    startResetUserPin() {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/pin-reset/start`;
        return this.apiClientHelper.postForPromise(url, {});
    }

    startChangePassword() {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/password-change/start`;
        return this.apiClientHelper.postForPromise(url, {});
    }

    initialPassCodeChange(): Promise<PasscodeChangeInitiatorObj> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/passcode-change/start`;
        return this.apiClientHelper.postForPromise(url, {});
    }

    generatePasscodeOtp(): Promise<PasscodeOtpResponse> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/passcode-change/otp/generate`;
        return this.apiClientHelper.postForPromise(url, {});
    }

    generateOtpForPasscodeRecovery(payload: { phoneNumber: string }): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/recover-passcode/otp/generate`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    changePasscodeOtp(payload: PasscodeOtpPayload): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/settings/passcode-change/otp/validate`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    changePasscodeV2(payload: PasscodeOtpPayload): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v2/settings/passcode`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    createNewPasscodeOtp(payload: NewPasscodePayload): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/user-device/register-passcode?bankingDomain=BUSINESS`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    getLoginOptionType(): Promise<LoginOptionTypeResponse> {
        const username = this.localStorage.get(StorageKeys.username);
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v2/auth/login-type?identityType=USERNAME&bankingDomain=BUSINESS&value=${username}`;
        return this.apiClientHelper.getForPromise(url);
    }

    markTimelineAsCompleted(timelines: TimelineModel[], step: number) {
        const stepTimeline = this.findTimelineByStep(timelines, step);
        if (!stepTimeline) {
            return;
        }
        stepTimeline.state = 'completed';
    }

    markNextTimelineAsActive(timelines: TimelineModel[], nextStep: number) {
        const nextStepTimeline = this.findTimelineByStep(timelines, nextStep);
        if (!nextStepTimeline) {
            return;
        }
        nextStepTimeline.state = 'active';
    }

    findTimelineByStep(timelines: TimelineModel[], step: number) {
        return timelines.find((t) => t?.step === step);
    }

    findTimelineByKey(timelines: TimelineModel[], key: string) {
        return timelines.find((t) => t?.key === key);
    }

    savePasscodeInTimeline(timelines: TimelineModel[], step: number, timelineData: any) {
        const timeline = this.findTimelineByStep(timelines, step);
        if (!timeline) {
            return;
        }
        timeline.data = timelineData;
    }

    generatePasswordOTP(data: any): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/recover-password/otp/generate`;
        return this.apiClientHelper.postForPromise(url, data, false);
    }

    validatePasswordOTP(data: any): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/recover-password/otp/validate`;
        return this.apiClientHelper.postForPromise(url, data, false);
    }

    changePassword(data: any): Promise<any> {
        const url = this.config.MONIEPOINT_HOST + `/api/v1/auth/recover-password`;
        return this.apiClientHelper.postForPromise(url, data, false);
    }

    recoverPasscodeV2(payload: any) {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v2/settings/recover-passcode`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    changePasswordV2(data: any): Promise<any> {
        const url = this.config.MONIEPOINT_USER_URL + `/api/v2/settings/recover-password`;
        return this.apiClientHelper.postForPromise(url, data, false);
    }

    startUnAuthenticated(payload: { phoneNumber: string }): Promise<TwoFactorAuthenticationStartResponse> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/credential-recovery/start?` + StringHelper.toUrlStringEncoded(payload);
        return this.apiClientHelper.getForPromise(url, false);
    }

    startAuthenticated(authenticationPurpose: string): Promise<TwoFactorAuthenticationStartResponse> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/start?authenticationPurpose=${authenticationPurpose}`;
        return this.apiClientHelper.getForPromise(url);
    }

    generateOtp2FAPublic(payload: TwoFactorGenerationPayload, otpPurpose: string): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/credential-recovery/otp/generate?otpPurpose=${otpPurpose}`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    generateOtp2FAAuth(otpPurpose: string, notificationChannel = ''): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/otp/generate?authenticationPurpose=${otpPurpose}&notificationChannel=${notificationChannel}`;
        return this.apiClientHelper.postForPromise(url, {});
    }

    generateReference2FA(payload: TwoFactorGenerationPayload): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/credential-recovery/generate-reference`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    generateReferenceAuth(livelinessPurpose: string, payload: GenerateReferenceApiPayload): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/generate-reference?authenticationPurpose=${livelinessPurpose}`;
        return this.apiClientHelper.postForPromise(url, payload);
    }

    ValidateOtp2FA(payload: TwoFactorGenerationPayload): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/credential-recovery/otp/validate`;
        return this.apiClientHelper.postForPromise(url, payload, false);
    }

    ValidateOtp2FAAuth(obj, otpPurpose: string): Promise<any> {
        const url = `${this.config.MONIEPOINT_USER_URL}/api/v1/auth/otp/validate?otpPurpose=` + otpPurpose;
        return this.apiClientHelper.postForPromise(url, obj);
    }

    resetTimeline(timelines: TimelineModel[]) {
        const STARTING_STEP = 1;
        timelines.forEach((timeline) => {
            timeline.state = 'inactive';
            this.clearObj(timeline?.data);
            if (timeline?.step === STARTING_STEP) timeline.state = 'active';
        });
    }

    clearObj(obj) {
        for (const [key] of Object.entries(obj)) {
            obj[key] = '';
        }
    }

    getDeviceInformation() {
        return this.deviceHelper.getDeviceInformation();
    }

    getUserTypePayload() {
        return this.localStorage.getUserTypePayload();
    }

    getAllCustomFields(): Promise<{ customFields: CustomTransactionField[] }> {
        let agentId = this.localStorage.get(StorageKeys.moniepointAgentId);
        let requestHeaders = { 'MP-POS-BUSINESS-ID': `${agentId}` };
        const url = `${this.config.MONIEPOINT_POS}/v1/custom-fields`;
        return this.apiClientHelper.getForPromise(url, true, false, false, requestHeaders);
    }

    createCustomFieldConfiguration(payload: CreateCustomTransactionField): Promise<any> {
        let agentId = this.localStorage.get(StorageKeys.moniepointAgentId);
        let requestHeaders = { 'MP-POS-BUSINESS-ID': `${agentId}` };
        const url = `${this.config.MONIEPOINT_POS}/v1/custom-fields`;
        return this.apiClientHelper.postForPromise(url, payload, true, false, false, 'WEB', requestHeaders);
    }

    updateCustomFieldConfiguration(payload: CreateCustomTransactionField, id: string): Promise<any> {
        let agentId = this.localStorage.get(StorageKeys.moniepointAgentId);
        let requestHeaders = { 'MP-POS-BUSINESS-ID': `${agentId}` };
        const url = `${this.config.MONIEPOINT_POS}/v1/custom-fields/${id}`;
        return this.apiClientHelper.putForPromise(url, payload, true, false, 'WEB', requestHeaders);
    }

    getCustomFieldDetails(fieldId: number | string): Promise<any> {
        let agentId = this.localStorage.get(StorageKeys.moniepointAgentId);
        let requestHeaders = { 'MP-POS-BUSINESS-ID': `${agentId}` };
        const url = `${this.config.MONIEPOINT_POS}/v1/custom-fields/${fieldId}`;
        return this.apiClientHelper.getForPromise(url, true, false, false, requestHeaders);
    }

    deleteCustomField(fieldId: number): Promise<any> {
        let agentId = this.localStorage.get(StorageKeys.moniepointAgentId);
        let requestHeaders = { 'MP-POS-BUSINESS-ID': `${agentId}` };
        const url = `${this.config.MONIEPOINT_POS}/v1/custom-fields/${fieldId}`;
        return this.apiClientHelper.deleteWithCustomHeader(url, false, requestHeaders);
    }
}

export interface PasswordChangePayload {
    otp?: string;
    oldPassword: string;
    newPassword: string;
    reference?: string;
}

interface PinChangePayload {
    otp: string;
    oldPin: string;
    newPin: string;
}
